import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { ROLES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";

const EMPTY = { email: "", name: "", role: "pm_produzione", password: "", phone: "", active: true, available: true, work_start: "08:00", work_end: "18:00", allowed_vehicle_ids: [], notes: "" };

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [ed, setEd] = useState(null);
  const load = () => api.get("/users").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setEd((e) => ({ ...e, [k]: v }));

  const save = async () => {
    try {
      if (ed.id) await api.put(`/users/${ed.id}`, ed);
      else await api.post("/users", ed);
      toast.success("Utente salvato"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (u) => { if (!window.confirm(`Eliminare ${u.name}?`)) return; try { await api.delete(`/users/${u.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-3xl text-lm-text">Utenti &amp; Permessi</h1><p className="text-sm text-lm-muted">{rows.length} utenti</p></div>
        <Button data-testid="new-user-btn" onClick={() => { setEd({ ...EMPTY }); setOpen(true); }} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream"><Plus className="w-4 h-4 mr-2" /> Nuovo utente</Button>
      </div>
      <div className="lm-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F4ECE1] text-lm-brown text-xs uppercase tracking-wider">
            <th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3 hidden sm:table-cell">Email</th><th className="text-left px-4 py-3">Ruolo</th><th className="text-left px-4 py-3 hidden sm:table-cell">Stato</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid={`user-${u.id}`} className="border-t border-lm-sandsoft/70">
                <td className="px-4 py-3 font-medium text-lm-text">{u.name}{u.is_demo && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-lm-sandsoft text-lm-muted">demo</span>}</td>
                <td className="px-4 py-3 text-lm-muted hidden sm:table-cell">{u.email}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-md bg-lm-sandsoft text-lm-brown font-semibold">{ROLES[u.role]}</span></td>
                <td className="px-4 py-3 hidden sm:table-cell"><span className={`text-xs ${u.active ? "text-[#166534]" : "text-[#991B1B]"}`}>{u.active ? "Attivo" : "Disattivato"}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => { setEd({ ...EMPTY, ...u, password: "" }); setOpen(true); }} className="p-2 text-lm-brown hover:bg-lm-sandsoft rounded-lg"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(u)} className="p-2 text-[#991B1B] hover:bg-lm-sandsoft rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg bg-lm-cream" data-testid="user-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl text-lm-brown flex items-center gap-2"><UserCog className="w-6 h-6" /> {ed.id ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3 py-2">
              <F label="Nome"><Input data-testid="u-name" value={ed.name} onChange={(e) => set("name", e.target.value)} className="bg-white" /></F>
              <F label="Email"><Input data-testid="u-email" type="email" value={ed.email} onChange={(e) => set("email", e.target.value)} className="bg-white" /></F>
              <F label="Ruolo">
                <Select value={ed.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="u-role" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </F>
              <F label="Telefono"><Input value={ed.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-white" /></F>
              <F label={ed.id ? "Nuova password (opz.)" : "Password"}><Input data-testid="u-password" type="password" value={ed.password || ""} onChange={(e) => set("password", e.target.value)} className="bg-white" /></F>
              {ed.role === "autista" && <>
                <F label="Inizio turno"><Input value={ed.work_start || ""} onChange={(e) => set("work_start", e.target.value)} placeholder="08:00" className="bg-white" /></F>
                <F label="Fine turno"><Input value={ed.work_end || ""} onChange={(e) => set("work_end", e.target.value)} placeholder="18:00" className="bg-white" /></F>
              </>}
              <div className="sm:col-span-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm"><Switch checked={ed.active} onCheckedChange={(v) => set("active", v)} /> Attivo</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={ed.available} onCheckedChange={(v) => set("available", v)} /> Disponibile</label>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="border-lm-sand text-lm-brown">Annulla</Button><Button data-testid="save-user-btn" onClick={save} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const F = ({ label, children }) => <div><Label className="text-xs text-lm-text2">{label}</Label><div className="mt-1">{children}</div></div>;
